import { Button, Checkbox, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, DialogTrigger, MessageBar, MessageBarBody, MessageBarTitle } from "@fluentui/react-components";
import { Delete16Regular, Dismiss20Regular } from "@fluentui/react-icons";
import { usePowder } from "../redux/ReduxContext";
import React, { useEffect, useRef, useState, useCallback } from "react";
import {
    GetElements,
    Element,
    GetInventoryGroupedByDescription,
    isSolveRawMaterialSuccessResponse,
    PowderInventoryGroupedByDescription,
    RawMaterialCalculationPayload,
    SolveRawMaterialCalculation,
    SolveRawMaterialError,
    SolveRawMaterialResponse,
    SolveRawMaterialResult,
    RawMaterialRowPayload,
} from "../../../services/PowderFormService";
import { PowderRawMaterialRow } from "./PowderRawMaterialRow";
import { useToast } from "../../ToastProvider";
import { useBalancedPercents } from "./useBalancedRatios";


import ElementPickerModal from "./ElementPickerModal";
import { RawMaterial } from "../redux/powderOrder";
import CustomDropdown from "../CustomDropdown";

const DropdownChevron = () => (
    <svg
        className="w-4 h-4 text-primary-dark dark:text-primary-light"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        viewBox="0 0 24 24"
    >
        <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M19 9l-7 7-7-7"
        />
    </svg>
);

const formatPercentDisplay = (value: number): string => {
    return value.toFixed(4).replace(".", ",");
};

type CalculationMessage = {
    intent: "success" | "error";
    title: string;
    lines: string[];
};

const buildFailureMessageContent = (response: SolveRawMaterialResponse | null): CalculationMessage => {
    const errorResponse = response as SolveRawMaterialError | null;
    const diagnostics = errorResponse?.diagnostics?.infeasibility;

    const details = [
        errorResponse?.message,
        errorResponse?.infeasibility_reason,
        diagnostics?.summary,
        ...(diagnostics?.reasons ?? []),
        diagnostics?.trim_note,
        errorResponse?.detail,
    ].filter((value): value is string => Boolean(value?.trim()));

    const uniqueDetails = details.filter((value, index) => details.indexOf(value) === index);
    const summary = uniqueDetails[0] || errorResponse?.message || "An unexpected error occurred.";

    const metaLines = [
        typeof errorResponse?.statusCode === "number" ? `Status code: ${errorResponse.statusCode}` : undefined,
        errorResponse?.solver_build ? `Solver build: ${errorResponse.solver_build}` : undefined,
    ].filter((value): value is string => Boolean(value));

    return {
        intent: "error",
        title: "Calculation failed",
        lines: [...uniqueDetails, ...metaLines].length > 0 ? [...uniqueDetails, ...metaLines] : [summary],
    };
};


interface PowderRawMaterialCalculationProps {

}

const tableHeaderText = 'text-xs  text-primary-dark dark:text-primary-light truncate text-left';
const ratioColumnWidthClass = "text-center w-[96px] min-w-[96px] max-w-[96px] sm:w-[96px] sm:min-w-[96px] sm:max-w-[96px]";
const lotColumnWidthClass = "w-[96px] min-w-[96px] max-w-[96px] sm:w-[168px] sm:min-w-[168px] sm:max-w-[168px]";

type RatioCellProps = {
    title?: string;
};

const RatioCell = ({ title }: RatioCellProps) => (
    <th className={`${ratioColumnWidthClass}`}>
        <div className={tableHeaderText}>
            {title}
        </div>
        <div className={`h-[48px] flex items-center justify-center p-1 bg-primary-light dark:bg-primary-dark`}>
            <span className="text-oi-button-blue dark:text-oi-powder-form-yellow text-xs font-semibold">-</span>
        </div>
    </th>
);

export default function PowderRawMaterialCalculation(props: PowderRawMaterialCalculationProps) {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [elementSearch, setElementSearch] = useState("");
    const [calculationMessage, setCalculationMessage] = useState<CalculationMessage | null>(null);
    const [isHeaderLotDropdownOpen, setIsHeaderLotDropdownOpen] = useState(false);

    const { showToast } = useToast();
    const { state, dispatch } = usePowder();
    const [allElements, setAllElements] = useState<Element[]>([]);
    const [selectedMaterial, setSelectedMaterial] = useState<string>('');
    const [selectedLot, setSelectedLot] = useState<string>('');
    const [inventory, setInventory] = useState<PowderInventoryGroupedByDescription[]>([]);
    const elementsScrollRef = useRef<HTMLDivElement | null>(null);
    const elementsScrollbarTrackRef = useRef<HTMLDivElement | null>(null);
    const elementsDragStateRef = useRef<{ startX: number; startThumbLeft: number } | null>(null);
    const lastCalculatedInputSignatureRef = useRef<string | null>(null);
    const headerLotTriggerRef = useRef<HTMLButtonElement | null>(null);
    const [elementsThumbWidth, setElementsThumbWidth] = useState(0);
    const [elementsThumbLeft, setElementsThumbLeft] = useState(0);
    const [isElementsThumbDragging, setIsElementsThumbDragging] = useState(false);

    const { updateValue, setBalance } = useBalancedPercents();
    const selectedMaterialLots = inventory.find((d) => d.Description === selectedMaterial)?.Lots ?? [];

    const calculationInputSignature = JSON.stringify({
        selectedMaterial,
        selectedLot,
        weightOnMillKg: state.weightOnMillKg,
        balanceElement: state.balanceElement,
        selectedElements: state.selectedElements,
        targetPercents: state.targetPercents?.map((target) => ({
            symbol: target.symbol,
            value: target.value,
            displayValue: target.displayValue,
        })),
        materials: state.materials.map((material) => ({
            role: material.role,
            lot: material.lot,
            raw_mtrl: material.raw_mtrl,
            rvid: material.rvid,
            trim_element: material.trim_element,
            ratio: material.ratio,
            pegRatio: material.pegRatio,
            recycledRatio: material.recycledRatio,
        })),
        prz: {
            enabled: state.prz.enabled,
            selected_lot: state.prz.selected_lot,
            ratio_pct: state.prz.ratio_pct,
        },
    });


    const updateScrollbar = useCallback(() => {
        const scrollEl = elementsScrollRef.current;
        const trackEl = elementsScrollbarTrackRef.current;
        if (!scrollEl || !trackEl) return;
        const { scrollWidth, clientWidth, scrollLeft } = scrollEl;
        const trackWidth = trackEl.clientWidth;
        if (scrollWidth <= clientWidth) {
            setElementsThumbWidth(0);
            setElementsThumbLeft(0);
            return;
        }
        const thumbWidth = Math.max((clientWidth / scrollWidth) * trackWidth, 32);
        const maxThumbLeft = trackWidth - thumbWidth;
        const thumbLeft = (scrollLeft / (scrollWidth - clientWidth)) * maxThumbLeft;
        setElementsThumbWidth(thumbWidth);
        setElementsThumbLeft(thumbLeft);
    }, []);

    useEffect(() => {
        const scrollEl = elementsScrollRef.current;
        if (!scrollEl) return;
        updateScrollbar();
        const handleScroll = () => updateScrollbar();
        scrollEl.addEventListener('scroll', handleScroll);
        window.addEventListener('resize', updateScrollbar);
        return () => {
            scrollEl.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', updateScrollbar);
        };
    }, [updateScrollbar]);

    useEffect(() => {
        updateScrollbar();
    });

    useEffect(() => {
        if (!isElementsThumbDragging) return;
        const scrollEl = elementsScrollRef.current;
        const trackEl = elementsScrollbarTrackRef.current;
        if (!scrollEl || !trackEl) return;
        const trackWidth = trackEl.clientWidth;
        const thumbWidth = elementsThumbWidth;
        const maxThumbLeft = trackWidth - thumbWidth;
        const maxScrollLeft = scrollEl.scrollWidth - scrollEl.clientWidth;

        const handlePointerMove = (e: PointerEvent) => {
            if (!elementsDragStateRef.current) return;
            const { startX, startThumbLeft } = elementsDragStateRef.current;
            const dx = e.clientX - startX;
            let newThumbLeft = Math.min(Math.max(startThumbLeft + dx, 0), maxThumbLeft);
            let newScrollLeft = (newThumbLeft / maxThumbLeft) * maxScrollLeft;
            if (isNaN(newScrollLeft)) newScrollLeft = 0;
            scrollEl.scrollLeft = newScrollLeft;
        };
        const handlePointerUp = () => {
            setIsElementsThumbDragging(false);
            elementsDragStateRef.current = null;
            window.removeEventListener('pointermove', handlePointerMove);
            window.removeEventListener('pointerup', handlePointerUp);
        };
        window.addEventListener('pointermove', handlePointerMove);
        window.addEventListener('pointerup', handlePointerUp);
        return () => {
            window.removeEventListener('pointermove', handlePointerMove);
            window.removeEventListener('pointerup', handlePointerUp);
        };
    }, [isElementsThumbDragging, elementsThumbWidth]);



    useEffect(() => {
        (async () => {
            const list = await GetElements();
            if (list) setAllElements(list);

            const inventory = await GetInventoryGroupedByDescription();
            console.log("Fetched inventory", inventory);
            if (inventory) {
                let cleanedInventory = inventory.map(item => ({ ...item, Lots: item.Lots.filter(lot => lot.Lot && lot.Lot.trim() !== "") }))
                setInventory(cleanedInventory);
            }
        })();
    }, []);

    const addMaterial = (materialDescription: string, lotValue: string) => {

        if (materialDescription !== '' && lotValue !== '') {
            if (state.materials.some(m => m.lot === lotValue && m.raw_mtrl === materialDescription)) {
                showToast({ title: 'Material already added', intent: 'warning', body: `The material ${materialDescription} with lot ${lotValue} has already been added.` });
                return;
            }
            if (state.materials.some(m => m.raw_mtrl?.includes("PRZ") && materialDescription.includes("PRZ"))) {
                showToast({ title: 'PRZ material already added', intent: 'warning', body: `A PRZ material has already been added. Only one PRZ material can be included in the calculation.` });
                return;
            }
            const lot = inventory.find((item) => item.Description === materialDescription)?.Lots.find((lotItem) => lotItem.Lot === lotValue);

            const rawMaterial: RawMaterial = {
                raw_mtrl: materialDescription,
                lot: lotValue,
                rvid: lot?.Rvid ?? "",
                role: "main",
                trim_element: "",
                inStockGrams: lot?.Qty ?? 0,
            }
            console.log("Adding material", rawMaterial);
            if (materialDescription.includes("PRZ")) {
                dispatch({ type: 'SET_FIELD', payload: { field: 'prz', value: { enabled: true, selected_lot: lotValue, ratio_pct: 0 } } });
            }
            if (materialDescription.includes("PRZ") || !state.materials.some(m => m.raw_mtrl?.includes("PRZ"))) {
                dispatch({ type: 'ADD_ARRAY_ITEM_LAST', payload: { field: 'materials', item: rawMaterial } });
            }
            else {
                dispatch({ type: 'ADD_ARRAY_ITEM', payload: { field: 'materials', item: rawMaterial } });
            }



            setSelectedMaterial('');
            setSelectedLot('');

        }
    }

    const pegRatioEnabled = () => {
        return state.materials.some((row) => row.raw_mtrl?.includes("PEG"));
    }

    const recycledRatioEnabled = () => {
        return state.materials.some((row) => row.raw_mtrl?.includes("PRZ"));
    }
    const totalRatioEnabled = (material: string = '') => {
        const materialCounts: Record<string, number> = {};
        state.materials.forEach((row) => {
            if (row.raw_mtrl) {
                materialCounts[row.raw_mtrl] = (materialCounts[row.raw_mtrl] || 0) + 1;
            }
        });
        return Object.values(materialCounts).some((count) => count > 1);
    }

    useEffect(() => {
        if (!calculationMessage || !lastCalculatedInputSignatureRef.current) {
            return;
        }

        if (lastCalculatedInputSignatureRef.current !== calculationInputSignature) {
            setCalculationMessage(null);
        }
    }, [calculationInputSignature, calculationMessage]);

    const removeElement = (element: string) => {
        dispatch({ type: 'DELETE_ARRAY_ITEM', payload: { field: 'selectedElements', index: state.selectedElements.indexOf(element) } })
        dispatch({ type: 'DELETE_ARRAY_ITEM', payload: { field: 'targetPercents', index: state.targetPercents?.findIndex(t => t.symbol === element) ?? -1 } })
    }

    const updateMaterialLot = (index: number, nextLot: string) => {
        const material = state.materials[index];

        if (!material?.raw_mtrl || !nextLot || material.lot === nextLot) {
            return;
        }

        if (state.materials.some((item, itemIndex) => itemIndex !== index && item.raw_mtrl === material.raw_mtrl && item.lot === nextLot)) {
            showToast({
                title: 'Material already added',
                intent: 'warning',
                body: `The material ${material.raw_mtrl} with lot ${nextLot} has already been added.`,
            });
            return;
        }

        const nextLotItem = inventory
            .find((item) => item.Description === material.raw_mtrl)
            ?.Lots.find((lotItem) => lotItem.Lot === nextLot);

        if (!nextLotItem) {
            showToast({
                title: 'Lot not found',
                intent: 'error',
                body: `Could not find inventory data for ${material.raw_mtrl} lot ${nextLot}.`,
            });
            return;
        }

        dispatch({
            type: 'UPDATE_ARRAY_ITEM',
            payload: {
                field: 'materials',
                index,
                value: (prevItem: RawMaterial) => ({
                    ...prevItem,
                    lot: nextLot,
                    rvid: nextLotItem.Rvid ?? '',
                    trim_element: prevItem.role === 'trim' ? nextLotItem.Trim ?? '' : '',
                    inStockGrams: nextLotItem.Qty ?? 0,
                    powderComposition: null,
                    oPercent: null,
                    cPercent: null,
                })
            }
        });

        if (material.raw_mtrl.includes("PRZ")) {
            dispatch({
                type: 'SET_FIELD',
                payload: {
                    field: 'prz',
                    value: {
                        ...state.prz,
                        enabled: true,
                        selected_lot: nextLot,
                    },
                },
            });
        }
    }

    const textClass = "text-primary-dark dark:text-primary-light";
    const cellHeightPx = 48;
    const expandButtonHeightPx = (state.materials.length) * cellHeightPx;

    const onCalculateClick = async () => {

        console.log("Calculating with state", state);
        if (!(state.materials.some(m => m.trim_element === "W") &&
            state.materials.some(m => m.trim_element === "C"))) {
            showToast({ persistent: true, title: 'Trim elements missing', intent: 'error', body: 'Please ensure that at least one material with trim element W and one material with trim element C are included before calculating.' });
            return;
        }
        console.log("Solve payload:", calculationPayload());


        setCalculationMessage(null);
        lastCalculatedInputSignatureRef.current = calculationInputSignature;
        const response = await SolveRawMaterialCalculation(calculationPayload());

        if (isSolveRawMaterialSuccessResponse(response)) {
            onSuccess(response);
        }
        else {
            onFailure(response);
            console.log(response)
        }

    }

    const onFailure = (response: SolveRawMaterialResponse | null) => {
        console.log("Calculation failed with response:", response);
        setCalculationMessage(buildFailureMessageContent(response));
    };

    const onSuccess = (response: SolveRawMaterialResult) => {
        setCalculationMessage({
            intent: "success",
            title: "Calculation succeeded",
            lines: [response?.message || "Calculation completed successfully."],
        });

        dispatch({ type: 'SET_FIELD', payload: { field: 'achieved_pct', value: response?.solution?.achieved_pct ?? {} } });

        response?.solution?.materials?.forEach(material => {
            state.materials.filter(m => m.lot === material.lot).forEach(m => {
                dispatch({
                    type: 'UPDATE_ARRAY_ITEM', payload: {
                        field: 'materials', index: state.materials.indexOf(m), value: (prevItem: RawMaterial) => ({
                            ...prevItem,
                            weightG: material.weight_g?.toString(),
                            ratioPercent: material.ratio_pct,
                        })
                    }
                });
            });
        });

        response?.included_elements_matrix?.rows?.forEach(material => {
            state.materials.filter(m => m.lot === material.lot).forEach(m => {
                dispatch({
                    type: 'UPDATE_ARRAY_ITEM', payload: {
                        field: 'materials', index: state.materials.indexOf(m), value: (prevItem: RawMaterial) => ({
                            ...prevItem,
                            contributionPercent: material.contrib_pct,
                        })
                    }
                });
            });
        });
        const responseElements: string[] = [];

        state.materials.forEach((_, index) => {
            dispatch({
                type: 'UPDATE_ARRAY_ITEM',
                payload: {
                    field: 'materials',
                    index,
                    value: (prevItem: RawMaterial) => ({
                        ...prevItem,
                        impurityExpansion: [],
                    })
                }
            });
        });

        response?.impurities_expansion?.per_material?.forEach(material => {
            state.materials
                .filter(m => m.lot === material.lot)
                .forEach(m => {
                    dispatch({
                        type: 'UPDATE_ARRAY_ITEM',
                        payload: {
                            field: 'materials',
                            index: state.materials.indexOf(m),
                            value: (prevItem: RawMaterial) => ({
                                ...prevItem,
                                impurityExpansion: material.non_selected_elements,
                            })
                        }
                    });

                    material.non_selected_elements?.forEach(ie => {
                        if (ie.element && !responseElements.includes(ie.element)) {
                            responseElements.push(ie.element);
                        }
                    });
                });
        });

        dispatch({
            type: 'SET_FIELD',
            payload: { field: 'responseElements', value: responseElements }
        });

        state.materials.forEach(m => {
            if (m.raw_mtrl?.includes("PEG")) {
                dispatch({
                    type: 'UPDATE_ARRAY_ITEM', payload: {
                        field: 'materials', index: state.materials.indexOf(m), value: (prevItem: RawMaterial) => ({
                            ...prevItem,
                            weightG: Number(m.pegRatio) ? (Number(m.pegRatio) * (Number(state.weightOnMillKg) * 1000) / 100) : prevItem.weightG,
                        })
                    }
                });
            }
        });

        console.log('state', state);
        console.log("Solve result:", response);
    }

    const calculationPayload = () => {

        const target_pct = state.targetPercents?.reduce((acc, t) => {
            if (t.symbol !== state.balanceElement) {
                acc[t.symbol] = t.value ?? 0;
            }
            return acc;
        }, {} as Record<string, number>) ?? {};

        const materials: RawMaterialRowPayload[] = [];
        state.materials.forEach((row) => {
            if (row.raw_mtrl?.includes("PEG")) return;
            if (row.role === "trim" && row.trim_element !== "") {
                if (!row.rvid?.trim() || !row.lot?.trim()) return;

                materials.push({
                    role: "trim",
                    lot: row.lot,
                    raw_mtrl: row.rvid,
                    trim_element: row.trim_element,
                });
            } else if (row.role === "main") {
                materials.push({
                    role: "main",
                    lot: row.lot,
                    raw_mtrl: row.rvid,
                });
            }
        });

        console.log(typeof state.weightOnMillKg)
        let totalWeight = Number(state.weightOnMillKg);
        if (state.weightOnMillKg.includes(",")) {
            totalWeight = Number(state.weightOnMillKg.replace(",", "."));
        }

        console.log(typeof state.weightOnMillKg)

        const payload: RawMaterialCalculationPayload = {
            total_weight: Number(totalWeight.toFixed(4)),
            weight_unit: 'kg',
            selected_elements: state.selectedElements,
            targets_pct: target_pct,
            balance_element: state.balanceElement ?? "",
            lump_impurities_into: "W",
            materials: materials,
            prz: {
                enabled: state.prz.enabled,
                selected_lot: state.prz.selected_lot ?? undefined,
                ratio_pct: state.prz.ratio_pct ?? undefined,
            },
        }
        return payload
    }

    const totalColumnCount = () => {
        let count = 4;

        if (state.materials.some(m => m.raw_mtrl?.includes("PRZ"))) count += 1;
        if (state.materials.some(m => m.raw_mtrl?.includes("PEG"))) count += 1;

        return count;
    }

    const actualRatio = () => {
        let total = 0;
        for (const achieved in state.achieved_pct) {
            total += state.achieved_pct[achieved] ?? 0;
        }
        let totalImpurities = 0;
        for (const material of state.materials) {
            totalImpurities += material.impurityExpansion?.reduce((acc, ie) => acc + (ie.contribution_to_batch_pct ?? 0), 0) ?? 0;
        }



        return total + totalImpurities;
    }


    return (
        <div className="bg-white dark:bg-oi-dark-background p-4 mb-4 drop-shadow-md">
            <div className="mb-2 flex items-center justify-between gap-3">
                <div className={`font-bold text-sm ${textClass}`}>Calculation raw material</div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-[auto_minmax(0,1fr)] items-start gap-0">
                <div ref={elementsScrollRef} className="relative w-full max-w-full pb-2 overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden  flex flex-row">
                    <div className="flex flex-col w-max">
                        <div className="flex overflow-x-scroll scroll-smooth">
                            <table className="table-fixed whitespace-nowrap border-separate border-spacing-0 text-left flex-none">
                                <thead className="flex">
                                    <div className="flex">


                                        <th className="w-8 flex-none ">
                                            <div className="h-[48px] w-7 items-center justify-center flex" />
                                        </th>

                                        <th className=" w-[104px] min-w-[104px] sm:w-[180px] sm:min-w-[180px] flex-col flex-none ">
                                            <div className={tableHeaderText}>
                                                Raw material
                                            </div>

                                            <input
                                                type="text"
                                                list={"powder-raw-material-options"}
                                                placeholder="Search raw material"
                                                value={selectedMaterial}
                                                onChange={(e) => {
                                                    setSelectedMaterial(e.currentTarget.value);
                                                    setSelectedLot('');
                                                }}
                                                onFocus={() => { setSelectedMaterial('') }}
                                                className={`font-normal outline-none w-full h-[48px] bg-primary-light dark:bg-primary-dark px-2 text-xs ${textClass}`}
                                                style={{ minWidth: 0 }}
                                            />

                                            <datalist id="powder-raw-material-options">
                                                {inventory && inventory.map((item, index) => (
                                                    <option key={index} value={item.Description} />
                                                ))}
                                            </datalist>
                                        </th>

                                        <th className={`${lotColumnWidthClass} overflow-hidden flex flex-col flex-none`}>
                                            <div className={tableHeaderText}>
                                                Lot
                                            </div>

                                            <button
                                                ref={headerLotTriggerRef}
                                                type="button"
                                                className="relative flex h-12 w-full items-center bg-primary-light pl-4 pr-10 text-left text-xs text-primary-dark dark:bg-primary-dark dark:text-primary-light"
                                                onClick={() => setIsHeaderLotDropdownOpen(!isHeaderLotDropdownOpen)}
                                            >
                                                <span className="truncate">
                                                    {selectedLot || "Select lot"}
                                                </span>
                                                <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2">
                                                    <DropdownChevron />
                                                </span>
                                            </button>

                                            <CustomDropdown
                                                value={selectedLot}
                                                onChange={(nextLot) => {
                                                    setSelectedLot(nextLot);
                                                    addMaterial(selectedMaterial, nextLot);
                                                }}
                                                options={selectedMaterialLots.map((lot) => ({
                                                    label: lot.Lot,
                                                    value: lot.Lot,
                                                    HasComposition: lot.HasComposition,
                                                }))}
                                                open={isHeaderLotDropdownOpen}
                                                onOpenChange={setIsHeaderLotDropdownOpen}
                                                targetRef={headerLotTriggerRef}
                                            />
                                        </th>

                                        <th className="flex flex-col w-[32px] flex-none">

                                            <div className={`${tableHeaderText} flex`}>
                                                Trim
                                            </div>
                                            <div className="bg-primary-light dark:bg-primary-dark flex items-center h-[48px]" />
                                        </th>

                                        {totalRatioEnabled() && <RatioCell title="Total ratio" />}
                                        {pegRatioEnabled() && <RatioCell title="PEG ratio" />}
                                        {recycledRatioEnabled() && <RatioCell title="Recycled ratio" />}

                                        {state.selectedElements.map((el) => (
                                            <th
                                                key={el}
                                                className="flex flex-col relative min-w-[84px] max-w-[84px] w-[84px] justify-end"
                                            >
                                                <div className="flex flex-col justify-around min-h-[48px] relative dark:bg-primary-dark bg-primary-light drop-shadow-sm">
                                                    <div className={`flex items-center justify-center ${textClass}`}>
                                                        {el}
                                                        <button
                                                            type="button"
                                                            className="mt-1 mr-1 p-0.5 opacity-60 hover:opacity-100 absolute top-2.5 right-0 rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-dark"
                                                            onClick={() => removeElement(el)}
                                                            aria-label={`Remove element ${el}`}
                                                            tabIndex={-1}
                                                        >
                                                            <Delete16Regular className="text-primary-dark dark:text-primary-light" />
                                                        </button>
                                                    </div>
                                                </div>
                                            </th>
                                        ))}

                                        {state.showImpurityExpansion && state.responseElements?.map((el) => (
                                            <th
                                                key={"element-" + el}
                                                className="flex flex-col relative min-w-[84px] max-w-[84px] w-[84px] justify-end"
                                            >
                                                <div className="flex flex-col justify-around min-h-[48px] relative dark:bg-primary-dark bg-primary-light">
                                                    <div className={`flex items-center justify-center ${textClass}`}>
                                                        {el}
                                                    </div>
                                                </div>
                                            </th>
                                        ))}

                                    </div>
                                </thead>

                                <tbody>
                                    {state.materials.map((row, index) => (
                                        <PowderRawMaterialRow
                                            inventory={inventory}
                                            onChangeLot={updateMaterialLot}
                                            pegRatioEnabled={pegRatioEnabled()}
                                            recycledRatioEnabled={recycledRatioEnabled()}
                                            totalRatioEnabled={totalRatioEnabled()}
                                            key={row.raw_mtrl + "-" + row.lot + "-" + index}
                                            index={index}
                                        />
                                    ))}
                                </tbody>
                            </table>
                            <table className="whitespace-nowrap border-separate border-spacing-0 text-left">
                                <thead className="flex">
                                    <th className="flex flex-col  justify-end   z-20 align-bottom min-w-[80px]">
                                        <div className="h-4"></div>
                                        <div className="flex flex-col">

                                            <Dialog open={dialogOpen} onOpenChange={(e, data) => setDialogOpen(data.open)}>
                                                <DialogTrigger>
                                                    <button
                                                        type="button"
                                                        className="h-[48px] w-full text-xs border-primary-dark dark:border-primary-dark text-primary-dark dark:text-primary-light bg-primary-light dark:bg-primary-dark hover:opacity-80"
                                                    >
                                                        Elements
                                                    </button>
                                                </DialogTrigger>

                                                <DialogSurface className="!flex !max-h-[90dvh] !w-[min(90vw,68rem)] !max-w-[90vw] !flex-col overflow-hidden">
                                                    <DialogBody className="flex min-h-0 flex-1 flex-col overflow-hidden">
                                                        <DialogTitle>Elements</DialogTitle>
                                                        <DialogContent className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
                                                            <ElementPickerModal
                                                                open={dialogOpen}
                                                                allElements={allElements}
                                                                elementSearch={elementSearch}
                                                                setElementSearch={setElementSearch}
                                                                selectedElements={state.selectedElements}
                                                            />
                                                        </DialogContent>

                                                        <DialogActions className="shrink-0 pt-3">
                                                            <button
                                                                className="bg-oi-button-blue text-primary-light p-2 px-6"
                                                                onClick={() => setDialogOpen(false)}>

                                                                OK
                                                            </button>
                                                        </DialogActions>
                                                    </DialogBody>
                                                </DialogSurface>
                                            </Dialog>
                                        </div>
                                    </th>
                                </thead>
                                <tbody className="">
                                    <tr className="flex">
                                        {state.responseElements?.length > 0 ? (
                                            <td className="focus:outline-none w-full flex-none focus:none">
                                                <button
                                                    type="button"
                                                    className="focus:outline-none w-full text-xs  border-primary-dark dark:border-primary-dark text-primary-dark dark:text-primary-light bg-primary-light dark:bg-primary-dark hover:opacity-80 whitespace-normal break-words "
                                                    style={{ height: `${expandButtonHeightPx - 2}px` }}
                                                    onClick={() => dispatch({ type: 'SET_FIELD', payload: { field: 'showImpurityExpansion', value: !state.showImpurityExpansion } })}
                                                >
                                                    {state.showImpurityExpansion ? "Collapse" : "Expand"}
                                                </button>
                                            </td>
                                        ) : null}
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        {state.targetPercents && state.targetPercents.length > 0 && (

                            <table id="balanceAndImpurities" className="table-fixed whitespace-nowrap border-separate border-spacing-0 text-left mt-2">
                                <tbody>
                                    <tr aria-hidden="true">
                                        <td className="h-5" colSpan={Math.max(totalColumnCount() + state.selectedElements.length + 1, 1)}></td>
                                    </tr>

                                    <tr className="h-[32px] align-top">
                                        <td className="w-8 min-w-[32px] max-w-[32px]"></td>
                                        <td className="w-[104px] min-w-[104px] sm:w-[180px] sm:min-w-[180px] text-xs text-primary-dark dark:text-primary-light text-left align-middle">Actual ratio
                                            <span className="ml-6">{
                                                actualRatio().toFixed(2)}</span>
                                        </td>
                                        <td className={lotColumnWidthClass}></td>
                                        <td className="w-[32px] min-w-[32px] max-w-[32px]"></td>
                                        {totalRatioEnabled() ? <td className={ratioColumnWidthClass}></td> : null}
                                        {pegRatioEnabled() ? <td className={ratioColumnWidthClass}></td> : null}
                                        {recycledRatioEnabled() ? <td className={ratioColumnWidthClass}></td> : null}

                                        {state.selectedElements.map((el) => (
                                            <td key={"element" + el} className=" min-w-[84px] max-w-[84px] w-[84px] align-middle">
                                                <div className="flex flex-col items-center">
                                                    <span className="text-oi-button-blue dark:text-oi-powder-form-yellow text-xs font-semibold">
                                                        {state.achieved_pct?.[el] !== undefined ? formatPercentDisplay(state.achieved_pct[el] ?? 0) : "-"}
                                                    </span>
                                                </div>
                                            </td>
                                        ))}
                                        {state.showImpurityExpansion && state.responseElements?.map((el) => (
                                            <td key={"element-" + el} className=" min-w-[84px] max-w-[84px] w-[84px] align-middle">
                                                <div className="flex flex-col items-center">
                                                    <span className="text-oi-button-blue dark:text-oi-powder-form-yellow text-xs font-semibold">
                                                        {
                                                            state.materials.reduce((sum, m) => {
                                                                const expansion = m.impurityExpansion ?? [];
                                                                const elementExpansion = expansion.find(ie => ie.element === el);
                                                                if (elementExpansion) {
                                                                    return sum + (elementExpansion.contribution_to_batch_pct ?? 0);
                                                                }
                                                                return sum;
                                                            }, 0).toFixed(4).replace('.', ',')
                                                        }
                                                    </span>
                                                </div>
                                            </td>
                                        ))}

                                        <td className="min-w-[80px]"></td>
                                    </tr>

                                    {/*Target*/}
                                    <tr className="h-[32px] align-top">
                                        <td className="w-8 min-w-[32px] max-w-[32px]"></td>
                                        <td className="w-[104px] min-w-[104px] sm:w-[180px] sm:min-w-[180px] text-xs text-primary-dark dark:text-primary-light text-left align-middle">Target ratio
                                            <span className="ml-6">{state.targetPercents?.reduce((sum, t) => sum + Number(t.value ?? 0), 0).toFixed(2)}</span>
                                        </td>
                                        <td className={lotColumnWidthClass}></td>
                                        <td className=" w-[32px] min-w-[32px] max-w-[32px] "></td>
                                        {totalRatioEnabled() ? <td className={ratioColumnWidthClass}></td> : null}
                                        {pegRatioEnabled() ? <td className={ratioColumnWidthClass}></td> : null}
                                        {recycledRatioEnabled() ? <td className={ratioColumnWidthClass}></td> : null}

                                        {state.targetPercents?.map((target, index) => {
                                            if (!state.targetPercents) return null;
                                            return (
                                                <td key={"target-" + target.symbol} className="min-w-[84px] max-w-[84px] w-[84px]">
                                                    <div className="flex flex-col items-center">
                                                        <input
                                                            type="text"
                                                            inputMode="decimal"
                                                            pattern="^[0-9]*[,.]?[0-9]*$"
                                                            disabled={state.targetPercents.findIndex(t => t.symbol === state.balanceElement) === index}
                                                            className="focus:outline-none  w-[80px] h-[28px] text-oi-button-blue dark:text-oi-powder-form-yellow text-xs font-semibold border border-primary-dark dark:border-primary-light bg-primary-light dark:bg-primary-dark px-2 text-center"

                                                            value={state.targetPercents[index]?.displayValue ?? state.targetPercents[index]?.value?.toString().replace('.', ',') ?? ''}
                                                            onChange={(e) => {
                                                                updateValue(target.symbol, e.target.value);
                                                            }}

                                                            placeholder="0"
                                                        />
                                                    </div>
                                                </td>
                                            );
                                        })}
                                        <td className="min-w-[80px]"></td>
                                    </tr>
                                    {/*Balance*/}
                                    <tr className="h-[32px] align-top">
                                        <td className="w-8 min-w-[32px] max-w-[32px]"></td>
                                        <td className="w-[104px] min-w-[104px] sm:w-[180px] sm:min-w-[180px] text-primary-dark dark:text-primary-light text-left text-xs align-middle">Balance</td>
                                        <td className={lotColumnWidthClass}></td>
                                        <td className="w-[32px] min-w-[32px] max-w-[32px]"></td>
                                        {totalRatioEnabled() ? <td className={ratioColumnWidthClass}></td> : null}
                                        {pegRatioEnabled() ? <td className={ratioColumnWidthClass}></td> : null}
                                        {recycledRatioEnabled() ? <td className={ratioColumnWidthClass}></td> : null}

                                        {state.targetPercents?.map((target, index) => {
                                            if (!state.targetPercents) return null;
                                            return (
                                                <td key={"target-" + target.symbol} className="min-w-[84px] max-w-[84px] w-[84px]">

                                                    <div className="flex flex-col items-center">
                                                        <label className="text-xs flex items-center justify-center text-primary-dark dark:text-white">
                                                            <Checkbox
                                                                checked={state.balanceElement === target.symbol}
                                                                onChange={(e) => {
                                                                    setBalance(e.target.checked ? target.symbol : null);
                                                                }}
                                                            />
                                                        </label>
                                                    </div>
                                                </td>
                                            );

                                        })}
                                        <td className="min-w-[80px]"></td>
                                    </tr>
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>

            </div>
            <div className="w-full mt-1">
                <div
                    ref={elementsScrollbarTrackRef}
                    className="relative h-3 rounded bg-gray-200 dark:bg-gray-700"
                    style={{ userSelect: 'none', height: '12px' }}
                    onPointerDown={(e) => {
                        const scrollEl = elementsScrollRef.current;
                        const trackEl = elementsScrollbarTrackRef.current;
                        if (!scrollEl || !trackEl) return;
                        const rect = trackEl.getBoundingClientRect();
                        const clickX = e.clientX - rect.left;
                        const trackWidth = trackEl.clientWidth;
                        const thumbWidth = elementsThumbWidth;
                        const maxThumbLeft = trackWidth - thumbWidth;
                        const maxScrollLeft = scrollEl.scrollWidth - scrollEl.clientWidth;
                        if (maxScrollLeft <= 0 || maxThumbLeft <= 0) return;
                        const nextThumbLeft = Math.max(0, Math.min(clickX - (thumbWidth / 2), maxThumbLeft));
                        scrollEl.scrollLeft = (nextThumbLeft / maxThumbLeft) * maxScrollLeft;
                    }}
                >
                    {elementsThumbWidth > 0 && (
                        <div
                            role="scrollbar"
                            aria-label="Horizontal scroll handle"
                            aria-controls="elements-scroll-area"
                            aria-valuenow={Math.round((elementsThumbLeft / Math.max(1, (elementsScrollbarTrackRef.current?.clientWidth || 1) - elementsThumbWidth)) * 100)}
                            tabIndex={0}
                            className="absolute top-0 h-3 rounded bg-oi-button-blue/90 dark:bg-oi-powder-form-yellow/90 cursor-grab active:cursor-grabbing"
                            style={{ width: `${elementsThumbWidth}px`, transform: `translateX(${elementsThumbLeft}px)`, height: '12px' }}
                            onPointerDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                elementsDragStateRef.current = {
                                    startX: e.clientX,
                                    startThumbLeft: elementsThumbLeft,
                                };
                                setIsElementsThumbDragging(true);
                            }}
                        />
                    )}
                </div>
            </div>
            {calculationMessage ? (
                <div className="mt-4">
                    <MessageBar intent={calculationMessage.intent} layout="multiline">
                        <MessageBarBody>
                            <div className="flex items-start justify-between gap-3">
                                <MessageBarTitle>{calculationMessage.title}</MessageBarTitle>
                                <Button
                                    appearance="subtle"
                                    size="small"
                                    aria-label="Close calculation message"
                                    onClick={() => setCalculationMessage(null)}
                                    icon={<Dismiss20Regular />}
                                />
                            </div>
                            <div className="mt-2 space-y-2 whitespace-pre-wrap break-words text-sm leading-5 max-h-[32vh] overflow-y-auto pr-2">
                                {calculationMessage.lines.map((line, index) => (
                                    <div key={`${calculationMessage.intent}-${index}-${line}`}>{line}</div>
                                ))}
                            </div>
                        </MessageBarBody>
                    </MessageBar>
                </div>
            ) : null}
            <div className="flex justify-end mt-4">
                <button className="bg-oi-button-blue p-2 text-primary-light" onClick={onCalculateClick}>Calculate</button>
            </div>
        </div >
    );
}
